/** QuickRouter — Assistant : fil de discussion du projet, tour en direct (texte, outils,
 *  statut), pièces jointes (images, PDF, Office, texte), palier rapide / puissant, arrêt.
 *  Pendant un tour, l’édition est verrouillée. */
import { useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { prepareImage, MAX_IMAGES, prepareDocument, droppedFiles, MAX_DOCS, DOC_ACCEPT, docIcon, fmtSize } from '../images.js';
import MiniMarkdown from './MiniMarkdown.jsx';

const EXAMPLES = [
  'Un shield VENTUNO Q : interface RS485 sur D0/D1 avec direction sur D2, bornier A B GND, terminaison 120 Ω sur cavalier — attention aux obstacles de la carte mère.',
  'Un nœud d’E/S ESP32-S3 de 70 × 50 : alimentation 5 V par bornier, régulateur 3,3 V, 4 entrées 24 V par optocoupleur et 4 sorties MOSFET.',
  'Un HAT Raspberry Pi avec un émetteur RS485 sur l’UART (GPIO14/15) et une LED d’état sur GPIO17.',
  'Route toute la carte, ajoute un plan de masse dessous et corrige les erreurs DRC.',
];

const TOOL_SHORT = {
  qr_get_project: 'lecture', qr_search_footprints: 'bibliothèque', qr_get_footprint: 'empreinte', qr_define_footprint: 'nouvelle empreinte',
  qr_set_board: 'carte', qr_place: 'placement', qr_update: 'déplacement', qr_delete: 'suppression', qr_connect: 'netlist',
  qr_delete_nets: 'nets supprimés', qr_autoroute: 'routage', qr_add_tracks: 'pistes', qr_clear_routes: 'effacement', qr_zone: 'plan de masse',
  qr_text: 'sérigraphie', qr_drc: 'DRC', qr_view: 'regard', ToolSearch: 'outils',
};

function Thumbs({ srcs }) {
  if (!srcs?.length) return null;
  return (
    <div className="msg-images">
      {srcs.map((src) => <a key={src} href={src} target="_blank" rel="noreferrer" title="Ouvrir l’image"><img src={src} alt="image jointe" /></a>)}
    </div>
  );
}

/** Documents d'un message : puces cliquables (téléchargement) ou simples (envoi en cours). */
function DocChips({ docs, url }) {
  if (!docs?.length) return null;
  return (
    <div className="doc-chips">
      {docs.map((d) => (url
        ? <a key={d.file} className="doc-chip" href={url(d)} title={`Télécharger ${d.name}${d.truncated ? ' (texte tronqué pour l’assistant)' : ''}`}>{docIcon(d.name)} {d.name} <span>{fmtSize(d.size)}{d.truncated ? ' · tronqué' : ''}</span></a>
        : <span key={d.id ?? d.name} className="doc-chip">{docIcon(d.name)} {d.name} <span>{fmtSize(d.size)}</span></span>))}
    </div>
  );
}

function ToolChips({ tools }) {
  if (!tools?.length) return null;
  const counts = new Map();
  for (const t of tools) counts.set(t, (counts.get(t) ?? 0) + 1);
  return (
    <div className="tool-chips">
      {[...counts.entries()].map(([t, n]) => <span key={t} className="chip">{TOOL_SHORT[t] ?? t}{n > 1 ? ` ×${n}` : ''}</span>)}
    </div>
  );
}

export default function ChatPanel() {
  const ai = useProjectStore((s) => s.ai);
  const send = useProjectStore((s) => s.sendPrompt);
  const cancel = useProjectStore((s) => s.cancelJob);
  const setTier = useProjectStore((s) => s.setTier);
  const clearChat = useProjectStore((s) => s.clearChat);
  const selection = useProjectStore((s) => s.selection);
  const readOnly = useProjectStore((s) => s.project?.role === 'viewer');
  const projectId = useProjectStore((s) => s.project?.id);
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [docs, setDocs] = useState([]);
  const [attachError, setAttachError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const listRef = useRef(null);
  const fileRef = useRef(null);
  const locked = !!ai.job || readOnly;

  /** Images et documents mêlés : chacun part dans sa file, dans la limite de chacune. */
  const addFiles = async (files) => {
    setAttachError('');
    if (!files.length) return;
    const errors = [];
    const imgs = [], dcs = [];
    let imgRoom = MAX_IMAGES - images.length, docRoom = MAX_DOCS - docs.length;
    for (const f of files) {
      try {
        if (f.type.startsWith('image/')) {
          if (imgRoom-- <= 0) { errors.push(`${MAX_IMAGES} images au plus par demande`); continue; }
          imgs.push(await prepareImage(f));
        } else {
          if (docRoom-- <= 0) { errors.push(`${MAX_DOCS} documents au plus par demande`); continue; }
          dcs.push(await prepareDocument(f));
        }
      } catch (e) { errors.push(e.message); }
    }
    if (errors.length) setAttachError([...new Set(errors)].join(' — '));
    setImages((cur) => [...cur, ...imgs]);
    setDocs((cur) => [...cur, ...dcs]);
  };
  const removeImage = (id) => setImages((cur) => {
    const gone = cur.find((x) => x.id === id);
    if (gone) URL.revokeObjectURL(gone.preview);
    return cur.filter((x) => x.id !== id);
  });

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ai.messages, ai.text, ai.job]);

  const hasAttach = images.length > 0 || docs.length > 0;
  const submit = () => {
    if ((!text.trim() && !hasAttach) || ai.job) return;
    const prompt = text.trim() || (docs.length
      ? 'Voici des documents : analyse-les et propose ce que tu peux en tirer pour la carte.'
      : 'Voici une image : analyse-la et propose ce que tu peux en tirer pour la carte.');
    send(prompt, images, docs);
    setText('');
    setImages([]);
    setDocs([]);
    setAttachError('');
  };
  const imgUrl = (file) => `/api/projects/${projectId}/chat/images/${file}`;
  const docUrl = (d) => `/api/projects/${projectId}/chat/files/${d.file}?name=${encodeURIComponent(d.name)}`;

  if (ai.available === false) {
    return (
      <div className="panel-body">
        <div className="empty">L’assistant n’est pas disponible sur ce serveur : le CLI <code>claude</code> est introuvable (variable <code>QUICKROUTER_CLAUDE_PATH</code>).</div>
      </div>
    );
  }

  return (
    <div
      className={`chat ${dragOver ? 'drag-over' : ''}`}
      onDragOver={(e) => { if (!locked && [...e.dataTransfer.types].includes('Files')) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!locked) addFiles(droppedFiles(e.dataTransfer.files)); }}
    >
      <div className="chat-list" ref={listRef}>
        {!ai.messages.length && !ai.job && (
          <div className="chat-intro">
            <p>Décrivez la carte voulue : l’assistant choisit le gabarit, conçoit le schéma, pose les composants, câble la netlist, route, contrôle (DRC) et regarde le résultat. Joignez fiches techniques (PDF), cahier des charges (DOCX), nomenclature ou liste d’E/S (XLSX), captures ou photos : 📎, glisser-déposer ou Ctrl+V.</p>
            {EXAMPLES.map((e) => <button key={e} className="example" onClick={() => setText(e)}>{e}</button>)}
          </div>
        )}
        {ai.messages.map((m, k) => (
          <div key={k} className={`msg ${m.role} ${m.error ? 'error' : ''}`}>
            {m.role === 'user' && m.by && <div className="msg-by">{m.by}</div>}
            <Thumbs srcs={m.images?.map(imgUrl)} />
            <DocChips docs={m.files} url={docUrl} />
            {m.role === 'assistant' ? <MiniMarkdown text={m.content} /> : <div className="msg-text">{m.content}</div>}
            {m.role === 'assistant' && <ToolChips tools={m.tools} />}
            {m.role === 'assistant' && m.durationMs != null && (
              <div className="msg-meta">{Math.round(m.durationMs / 1000)} s{m.costUsd ? ` · ${m.costUsd.toFixed(3)} $` : ''}</div>
            )}
          </div>
        ))}
        {ai.job && (
          <>
            {ai.pending && <div className="msg user"><Thumbs srcs={ai.pendingImages} /><DocChips docs={ai.pendingDocs} /><div className="msg-text">{ai.pending}</div></div>}
            <div className="msg assistant live">
              {ai.text && <MiniMarkdown text={ai.text} />}
              <ToolChips tools={ai.tools} />
              <div className="msg-status"><span className="spinner" /> {ai.status || 'Travail en cours…'}</div>
            </div>
          </>
        )}
        {ai.error && <div className="msg assistant error"><div className="msg-text">⚠ {ai.error}</div></div>}
      </div>
      <div className="chat-input">
        {hasAttach && (
          <div className="attach-row">
            {images.map((im) => (
              <div key={im.id} className="attach" title={`${im.name} — ${im.size[0]}×${im.size[1]}`}>
                <img src={im.preview} alt={im.name} />
                <button className="attach-x" title="Retirer" onClick={() => removeImage(im.id)}>✕</button>
              </div>
            ))}
            {docs.map((d) => (
              <div key={d.id} className="attach-doc" title={`${d.name} — ${fmtSize(d.size)}`}>
                <span className="grow">{docIcon(d.name)} {d.name}</span>
                <span className="muted">{fmtSize(d.size)}</span>
                <button className="icon" title="Retirer" onClick={() => setDocs((cur) => cur.filter((x) => x.id !== d.id))}>✕</button>
              </div>
            ))}
          </div>
        )}
        {attachError && <div className="warn small">{attachError}</div>}
        <textarea
          rows={3}
          value={text}
          placeholder={readOnly ? 'Projet en lecture seule : l’assistant ne peut pas le modifier.' : ai.job ? 'L’assistant travaille… (édition verrouillée)' : `Votre demande${selection.length ? ` (sélection : ${selection.length} élément${selection.length > 1 ? 's' : ''})` : ''} — Ctrl+V pour coller, Ctrl+Entrée pour envoyer`}
          disabled={!!ai.job || readOnly}
          onChange={(e) => setText(e.target.value)}
          onPaste={(e) => {
            const files = droppedFiles(e.clipboardData?.items);
            if (files.length) { e.preventDefault(); addFiles(files); }
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
          }}
        />
        {dragOver && <div className="drop-hint">Déposez images et documents (PDF, DOCX, XLSX…) pour les joindre à la demande</div>}
        <div className="row">
          <div className="seg">
            <button className={ai.tier === 'fast' ? 'active' : ''} onClick={() => setTier('fast')} title="Modèle rapide">Rapide</button>
            <button className={ai.tier === 'strong' ? 'active' : ''} onClick={() => setTier('strong')} title="Modèle le plus capable (plus lent)">Puissant</button>
          </div>
          <button className="icon" disabled={locked} title={`Joindre des images (${MAX_IMAGES}) ou des documents (${MAX_DOCS}) : PDF, DOCX, XLSX, PPTX, TXT, CSV… — ou collez (Ctrl+V), ou glissez-les ici`} onClick={() => fileRef.current?.click()}>📎</button>
          <input ref={fileRef} type="file" accept={`image/png,image/jpeg,image/webp,image/gif,${DOC_ACCEPT}`} multiple hidden onChange={(e) => { const f = [...(e.target.files ?? [])]; e.target.value = ''; addFiles(f); }} />
          <span className="grow" />
          {ai.messages.length > 0 && !ai.job && <button className="icon" title="Effacer le fil" onClick={() => window.confirm('Effacer tout le fil de discussion de ce projet ?') && clearChat()}>🗑</button>}
          {ai.job
            ? <button className="danger" onClick={cancel}>■ Arrêter</button>
            : <button className="primary" disabled={(!text.trim() && !hasAttach) || readOnly} onClick={submit}>Envoyer</button>}
        </div>
      </div>
    </div>
  );
}
